import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Grid, makeStyles, Typography } from '@material-ui/core';

import { useProjectApi } from 'src/services';
import { AnalyticCard } from 'src/scenes/Project/components';
import { ProjectMetrics, LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { ContentContainer, LabelChip, MapWrapper, TitleContainer } from 'src/components';
import { useFetchStatus } from 'src/hooks';
import { buildLeafletLayer } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { useRouteMatch } from 'react-router';
import { upperCaseOnlyFirstLetter as ucofl } from 'src/utils/formatters';


const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 16,
  doubleClickZoom: false,
  attributionControl: false,
};

const useStyles = makeStyles(theme => ({
  map: {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  analyticCardPaper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  isicLabel: {
    width: '56px',
  },
}));

/*const impacts = [
  {
    componentLabel: 'Tierra',
    natureStateLabel: 'Proporción de vegetación natural',
    natureStateStatus: 'MA',
    pressureCategoryLabel: 'Cambio de uso de suelo',
    pressureCategoryStatus: 'MA',
    impactCategoryLabel: 'Degradación de la tierra',
    impactCategoryStatus: 'MA',
  },
  {
    componentLabel: 'Agua dulce',
    natureStateLabel: 'Disponibilidad de agua dulce',
    natureStateStatus: 'B',
    pressureCategoryLabel: 'Uso de agua dulce',
    pressureCategoryStatus: 'A',
    impactCategoryLabel: 'Escasez hídrica',
    impactCategoryStatus: 'M',
  },
  {
    componentLabel: 'Biodiversidad',
    natureStateLabel: 'Integridad de la biodiversidad',
    natureStateStatus: 'MA',
    pressureCategoryLabel: 'Introducción de especies',
    pressureCategoryStatus: 'MB',
    impactCategoryLabel: 'Perdida de la biodiversidad',
    impactCategoryStatus: 'M',
  },
];*/

const interestAreaMapHeight = 30;

const heightVH = n => document.documentElement.clientHeight * (n / 100);

const ProjectPressuresAndDependenciesContainer = ({ interestArea, project }) => {

  const [ isic, setIsic ] = useState({});

  const { isLoading: isLoadingInterestArea, isSuccess: isSuccessInterestArea } = interestArea.fetchStatus;
  const match = useRouteMatch();
  const classes = useStyles();
  const projectApi = useProjectApi();

  const { projectId } = match.params;

  // const [ natureVariation, setNatureVariation ] = useState({});
  const [ indicators, setIndicators ] = useState({});
  const [ rcas, setRcas ] = useState([]);

  const { isLoading: isLoadingIsic, isSuccess: isSuccessIsic, handleConfirm: fetchIsic } = useFetchStatus({
    confirmFn: async () => {
      const isic = await projectApi.getIsic(projectId);
      setIsic(isic);
    },
  });

  useEffect(() => {
    fetchIsic();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { isLoading: isLoadingProjectMetrics, handleConfirm: fetchProjectMetrics } = useFetchStatus({
    confirmFn: async () => {
      const [ generalInfo, rcas ] = await Promise.all([
        projectApi.getGeneralInfo({ projectId }),
        projectApi.rcaSearch({ projectId, params: { limit: 0 } }),
      ]);

      if (generalInfo) {
        setIndicators({
          campaignsCount: generalInfo.nCampaigns,
          replicaStationsCount: generalInfo.nStationReplicate,
        });
      }

      setRcas(rcas.map(r => r.id));
    },
  });

  useEffect(() => {
    fetchProjectMetrics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasIsic = Boolean(isic?.id);

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        project={project}
        breadcrumbs={[ 'projects', 'project', { name: 'Visión general' } ]}>
        Impactos y dependencias - Visión general
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        {(!hasIsic && isSuccessIsic) &&
          <Grid container spacing={1} item>
            <Grid item xs={12}>
              <Box my={2}>
                <Typography variant="h6" color="textSecondary" align="center">
                  No se ha definido una clasificación ISIC para este proyecto.
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  Para visualizar la información de impactos y dependencias primero debe definir una clasificación ISIC.
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  Puede hacerlo desde la sección de <strong>Clasificación ISIC</strong> en el menú del proyecto.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        }
        {hasIsic &&
          <>
            <Grid container spacing={1} item>
              <Grid item xs={12}>
                <AnalyticCard title="Métricas SMA">
                  <Divider />
                  <ProjectMetrics
                    indicators={indicators}
                    rcas={rcas}
                    isLoading={isLoadingProjectMetrics}
                  />
                </AnalyticCard>
              </Grid>
            </Grid>
            <Box my={0.5} />
            <Grid container spacing={1} item>
              <Grid item xs={6}>
                <AnalyticCard title="Área de interés" paperProps={{ className: classes.analyticCardPaper }}>
                  <Divider />
                  <Box mt={0.5} />
                  {isLoadingInterestArea && <LoadingBox label="área de interes" />}
                  {isSuccessInterestArea &&
                    <MapWrapper
                      id="interest-area-map"
                      className={classes.map}
                      options={mapOptions}
                      height={isLoadingInterestArea ? 0 : heightVH(interestAreaMapHeight)}
                      layers={
                        interestArea.data.map(amd => buildLeafletLayer({
                          features: amd.geojson,
                          id: amd.id,
                          name: amd.name,
                        }))
                      }
                      fitBoundsOnUpdate
                    />
                  }
                </AnalyticCard>
              </Grid>
              <Grid container direction="column" item xs={6}>
                <Grid item>
                  <AnalyticCard title="Clasificación ISIC">
                    <Divider />
                    <Box mt={2} />
                    {isLoadingIsic && <LoadingBox label="ISIC" />}
                    {isSuccessIsic &&
                      <Box display="flex" flexDirection="column">
                        <Box display="flex" alignItems="center" visibility={isic.uniqueCode ? 'visible' : 'hidden'}>
                          <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                            Código:
                          </Typography>
                          <LabelChip variant="outlined" label={ucofl(isic.uniqueCode)} color="default" />
                        </Box>
                        <Box display="flex" alignItems="center" visibility={isic.sectionEsp ? 'visible' : 'hidden'}>
                          <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                            Sección:
                          </Typography>
                          <LabelChip variant="outlined" label={ucofl(isic.sectionEsp)} color="default" />
                        </Box>
                        <Box display="flex" alignItems="center" visibility={isic.divisionEsp ? 'visible' : 'hidden'}>
                          <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                            División:
                          </Typography>
                          <LabelChip variant="outlined" label={ucofl(isic.divisionEsp)} color="default" />
                        </Box>
                        <Box display="flex" alignItems="center" visibility={isic.groupEsp ? 'visible' : 'hidden'}>
                          <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                            Grupo:
                          </Typography>
                          <LabelChip variant="outlined" label={ucofl(isic.groupEsp)} color="default" />
                        </Box>
                        <Box display="flex" alignItems="center" visibility={isic.classEsp ? 'visible' : 'hidden'}>
                          <Typography className={classes.isicLabel} variant="body2" color="textSecondary" gutterBottom>
                            Clase:
                          </Typography>
                          <LabelChip variant="outlined" label={ucofl(isic.classEsp)} color="default" />
                        </Box>
                      </Box>
                    }
                  </AnalyticCard>
                </Grid>
              </Grid>
            </Grid>
          </>
        }
      </ContentContainer>
    </Grid>
  );
};

ProjectPressuresAndDependenciesContainer.propTypes = {
  interestArea: PropTypes.shape({
    data: PropTypes.array,
    fetchStatus: PropTypes.object,
  }).isRequired,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export { ProjectPressuresAndDependenciesContainer };
